import React, { useState } from "react";

interface OMButtonProps {
  onClick: React.MouseEventHandler<HTMLButtonElement>;
  isDisabled?: boolean;
  label: string;
  hidden?: boolean;
  colour?: string;
  leftIcon?: React.JSX.Element;
  rightIcon?: React.JSX.Element;
  additionalClassName?: string;
  type?: ButtonTypes;
}

export type ButtonTypes =
  | "primary"
  | "secondary"
  | "tertiary"
  | "rounded"
  | "logout"
  | "danger"
  | "danger-filled";

export const Button = (props: OMButtonProps) => {
  const {
    isDisabled,
    hidden,
    onClick,
    label,
    colour,
    leftIcon,
    rightIcon,
    additionalClassName,
    type,
  } = props;

  const [isHovered, setIsHovered] = useState(false);

  const className = colour
    ? "flex items-center px-6 py-2 text-white bg-red-500 rounded-lg shadow-md hover:bg-red-600 transition text-base"
    : "flex items-center px-6 py-3 text-white bg-blue-600 rounded-lg shadow-md disabled:bg-gray-300 hover:bg-blue-700 transition text-base";

  if (hidden) {
    return null;
  }

  if (type === "rounded") {
    return (
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`flex gap-2 justify-center items-center px-6 py-3 h-7 text-gray-550 bg-white rounded-full border-[2px] w-full border-gray-200 hover:border-gray-376 disabled:opacity-40 font-outfit font-medium text-[14px] 
          focus:ring-2 focus:ring-offset-2 focus:ring-ominimo-400 ${additionalClassName ?? ""}`}>
        {leftIcon}
        {label}
        {rightIcon}
      </button>
    );
  }

  if (type === "secondary") {
    return (
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`flex gap-2 justify-center items-center px-6 py-3 h-11 text-ominimo-400 hover:text-blue-750 bg-white rounded-md border-2 w-full border-ominimo-400  disabled:opacity-40 disabled:hover:text-ominimo-400 font-outfit font-medium text-base 
          focus:ring-2 focus:ring-offset-2 focus:ring-ominimo-400 ${additionalClassName ?? ""}`}>
        {leftIcon}
        {label}
        {rightIcon}
      </button>
    );
  }

  if (type === "logout") {
    return (
      <button
        onClick={onClick}
        disabled={isDisabled}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        className={`flex gap-2 justify-center items-center px-2 py-0 h-8 hover:text-red-500 bg-white rounded-[4px] border-[2px] w-full hover:border-red-500 border-gray-200  disabled:opacity-40 disabled:hover:text-ominimo-400 font-outfit font-medium text-base 
          focus:ring-2 focus:ring-offset-2 focus:ring-ominimo-400 ${additionalClassName ?? ""}`}>
        {isHovered && leftIcon}
        <p
          className={`font-normal text-[15px] leading-[text-4] tracking-[0.3px] ${isHovered ? "hover:text-red-500" : "text-gray-550"}`}>
          {label}
        </p>
        {isHovered && rightIcon}
      </button>
    );
  }

  if (type === "danger") {
    return (
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`flex gap-2 justify-center items-center px-4 py-0 h-11 text-red-500 bg-white rounded-[6px] border-[2px] border-red-500 disabled:opacity-40 disabled:hover:text-ominimo-400 font-outfit font-medium text-base 
          focus:ring-2 focus:ring-offset-2 focus:ring-ominimo-400 ${additionalClassName ?? ""}`}>
        <p className={`font-medium text-[15px] leading-[text-4] tracking-[0.3px] text-red-500`}>
          {label}
        </p>
      </button>
    );
  }

  if (type === "danger-filled") {
    return (
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`flex gap-2 justify-center items-center px-4 py-0 h-11 text-white bg-red-500 hover:bg-red-450 rounded-[6px] disabled:opacity-40 disabled:hover:text-ominimo-400 font-outfit font-medium text-base 
          focus:ring-2 focus:ring-offset-2 focus:ring-ominimo-400 ${additionalClassName ?? ""}`}>
        <p className={`font-medium text-[15px] leading-[text-4] tracking-[0.3px] text-white`}>
          {label}
        </p>
      </button>
    );
  }

  if (type === "primary") {
    return (
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`flex gap-2 justify-center items-center px-4 py-3 h-11 min-w-[103px] text-white bg-ominimo-400 hover:bg-blue-750 rounded-md disabled:opacity-40 disabled:hover:text-ominimo-400 font-outfit font-medium text-base 
      focus:ring-2 focus:ring-offset-2 focus:ring-ominimo-400 transition-colors duration-200 ease-in-out ${additionalClassName ?? ""}`}>
        {leftIcon}
        {label}
        {rightIcon}
      </button>
    );
  }

  if (type === "tertiary") {
    return (
      <button
        onClick={onClick}
        disabled={isDisabled}
        className={`flex gap-2 justify-center items-center px-4 py-3 h-11 min-w-[103px] text-gray-550 bg-gray-100 hover:bg-gray-200 hover:text-black rounded-md disabled:hover:bg-transparent disabled:opacity-40 disabled:hover:text-ominimo-400 font-outfit font-medium text-base 
      focus:ring-2 focus:ring-offset-2 focus:ring-ominimo-400 ${additionalClassName ?? ""}`}>
        {leftIcon}
        {label}
        {rightIcon}
      </button>
    );
  }

  return (
    <button
      onClick={onClick}
      disabled={isDisabled}
      className={`${className} ${additionalClassName ?? ""}`}>
      {leftIcon}
      {label}
      {rightIcon}
    </button>
  );
};
